import { TFunction } from 'i18next';
import { Component, FormEvent } from 'react';
import { VisuallyHidden } from 'react-aria';
import { WrappedFieldProps } from 'redux-form';
import Input from 'shared/components/Input';
import MaterialInput from 'shared/components/MaterialInput';
import { SPACINGS } from 'shared/styles/variables';
import styled, { DefaultTheme, withTheme } from 'styled-components';

import { errorStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputStyles } from 'lib/interfaces/input-styles';
import MaterialSelect from 'verify/components/MaterialSelect';
import Select from 'verify/components/Select';

interface ContainerProps {
  inputStyle: InputStyles;
}

const Container = styled('div')<ContainerProps>`
  display: flex;
  flex-wrap: wrap;
  margin-left: -${SPACINGS.base}px;
  margin-right: -${SPACINGS.base}px;
  margin-bottom: ${SPACINGS.base * 2}px;
  ${({ inputStyle }) =>
    inputStyle === InputStyles.Material ? `padding-top: ${SPACINGS.base * 2}px;` : ''};
`;

const OutlinedLabelContainer = styled('div')`
  &:focus-within label {
    color: ${(props) => props.theme.colorPrimary};
  }
`;

const FormError = styled('div')`
  ${errorStyles}
  padding-left: ${SPACINGS.base}px;
`;

const Month = styled('div')`
  flex: 0 0 40%;
  max-width: 40%;
  padding-left: ${SPACINGS.base}px;
  padding-right: ${SPACINGS.base}px;
  text-transform: capitalize;
`;

const DayOrYear = styled('div')`
  flex: 0 0 30%;
  max-width: 30%;
  padding-left: ${SPACINGS.base}px;
  padding-right: ${SPACINGS.base}px;
`;

interface ThemedFormDateInputProps extends FormControlProps {
  disabled?: boolean;
  theme: DefaultTheme;
  t: TFunction;
}

interface DeserializedDate {
  year: string;
  month: string;
  day: string;
}

type DateInputState = DeserializedDate;

class ThemedFormDateInput extends Component<
  ThemedFormDateInputProps & WrappedFieldProps,
  DateInputState
> {
  constructor(props: ThemedFormDateInputProps & WrappedFieldProps) {
    super(props);

    this.state = {
      day: '',
      month: '',
      year: '',
    };
  }

  public render() {
    const {
      theme: { inputStyle },
    } = this.props;

    switch (inputStyle) {
      case InputStyles.Material:
        return this.renderMaterial();
      case InputStyles.Outlined:
      default:
        return this.renderOutlined();
    }
  }

  private renderOutlined() {
    const {
      disabled,
      input,
      t,
      meta: { error, submitFailed },
      theme: { inputStyle },
    } = this.props;

    const hasError = !!(error && submitFailed);

    return (
      <OutlinedLabelContainer>
        <label>{t('database.form.birthdate-label')}</label>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <Container inputStyle={inputStyle}>
          <Month>
            <VisuallyHidden>
              <label htmlFor={input.name + '-month'}>
                {`${t('database.form.birthdate-label')}, ${t('form.date-input.month.placeholder')}`}
              </label>
            </VisuallyHidden>
            <Select
              name={input.name + '-month'}
              disabled={disabled}
              hasError={hasError}
              onChange={this.handleChangeMonth}
              aria-label={t('form.date-input.month.placeholder')}
              value={this.deserializedValue.month}
            >
              <option value='' selected disabled>
                {t('form.date-input.month.placeholder')}
              </option>
              <option value={0}>{t('form.date-input.month.option.january')}</option>
              <option value={1}>{t('form.date-input.month.option.february')}</option>
              <option value={2}>{t('form.date-input.month.option.march')}</option>
              <option value={3}>{t('form.date-input.month.option.april')}</option>
              <option value={4}>{t('form.date-input.month.option.may')}</option>
              <option value={5}>{t('form.date-input.month.option.june')}</option>
              <option value={6}>{t('form.date-input.month.option.july')}</option>
              <option value={7}>{t('form.date-input.month.option.august')}</option>
              <option value={8}>{t('form.date-input.month.option.september')}</option>
              <option value={9}>{t('form.date-input.month.option.october')}</option>
              <option value={10}>{t('form.date-input.month.option.november')}</option>
              <option value={11}>{t('form.date-input.month.option.december')}</option>
            </Select>
          </Month>
          <DayOrYear>
            <VisuallyHidden>
              <label htmlFor={input.name + '-day'}>
                {`${t('database.form.birthdate-label')}, ${t('form.date-input.day')}`}
              </label>
            </VisuallyHidden>
            <Input
              name={input.name + '-day'}
              disabled={disabled}
              hasError={hasError}
              min={0}
              max={31}
              onChange={this.handleChangeDay}
              aria-label={t('form.date-input.day')}
              placeholder={t('form.date-input.day')}
              type='number'
              // Default to empty string so it starts as a controlled input
              value={this.deserializedValue.day}
            />
          </DayOrYear>
          <DayOrYear>
            <VisuallyHidden>
              <label htmlFor={input.name + '-year'}>
                {`${t('database.form.birthdate-label')}, ${t('form.date-input.year')}`}
              </label>
            </VisuallyHidden>
            <Input
              name={input.name + '-year'}
              disabled={disabled}
              hasError={hasError}
              min={1900}
              max={new Date().getFullYear()}
              onChange={this.handleChangeYear}
              aria-label={t('form.date-input.year')}
              placeholder={t('form.date-input.year')}
              type='number'
              // Default to empty string so it starts as a controlled input
              value={this.deserializedValue.year}
            />
          </DayOrYear>
          {hasError && <FormError role='alert'>{error}</FormError>}
        </Container>
      </OutlinedLabelContainer>
    );
  }

  private renderMaterial() {
    const {
      disabled,
      input,
      t,
      meta: { error, submitFailed },
      theme: { inputStyle },
    } = this.props;

    const hasError = !!(error && submitFailed);

    return (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      <Container inputStyle={inputStyle}>
        <Month>
          <VisuallyHidden>
            <label htmlFor={input.name + '-month'}>
              {`${t('database.form.birthdate-label')}, ${t('form.date-input.month.placeholder')}`}
            </label>
          </VisuallyHidden>
          <MaterialSelect
            name={input.name + '-month'}
            disabled={disabled}
            hasError={hasError}
            onChange={this.handleChangeMonth}
            value={this.deserializedValue.month}
            labelText={t('database.form.birthdate-label')}
          >
            <option value='' selected disabled>
              {t('form.date-input.month.placeholder')}
            </option>
            <option value={0}>{t('form.date-input.month.option.january')}</option>
            <option value={1}>{t('form.date-input.month.option.february')}</option>
            <option value={2}>{t('form.date-input.month.option.march')}</option>
            <option value={3}>{t('form.date-input.month.option.april')}</option>
            <option value={4}>{t('form.date-input.month.option.may')}</option>
            <option value={5}>{t('form.date-input.month.option.june')}</option>
            <option value={6}>{t('form.date-input.month.option.july')}</option>
            <option value={7}>{t('form.date-input.month.option.august')}</option>
            <option value={8}>{t('form.date-input.month.option.september')}</option>
            <option value={9}>{t('form.date-input.month.option.october')}</option>
            <option value={10}>{t('form.date-input.month.option.november')}</option>
            <option value={11}>{t('form.date-input.month.option.december')}</option>
          </MaterialSelect>
        </Month>
        <DayOrYear>
          <VisuallyHidden>
            <label htmlFor={input.name + '-day'}>
              {`${t('database.form.birthdate-label')}, ${t('form.date-input.day')}`}
            </label>
          </VisuallyHidden>
          <MaterialInput
            name={input.name + '-day'}
            disabled={disabled}
            hasError={hasError}
            min={0}
            max={99}
            onChange={this.handleChangeDay}
            type='number'
            // Default to empty string so it starts as a controlled input
            value={this.deserializedValue.day}
            labelText={t('form.date-input.day')}
          />
        </DayOrYear>
        <DayOrYear>
          <VisuallyHidden>
            <label htmlFor={input.name + '-year'}>
              {`${t('database.form.birthdate-label')}, ${t('form.date-input.year')}`}
            </label>
          </VisuallyHidden>
          <MaterialInput
            name={input.name + '-year'}
            disabled={disabled}
            hasError={hasError}
            min={1900}
            max={9999}
            onChange={this.handleChangeYear}
            labelText={t('form.date-input.year')}
            type='number'
            // Default to empty string so it starts as a controlled input
            value={this.deserializedValue.year}
          />
        </DayOrYear>
        {hasError && <FormError role='alert'>{error}</FormError>}
      </Container>
    );
  }

  private handleChangeDay = (event: FormEvent<HTMLInputElement>) => {
    const newValue = {
      ...this.deserializedValue,
      day: (event.target as HTMLInputElement).value,
    };

    this.propagateDateChange(newValue);
  };

  private handleChangeMonth = (event: FormEvent<HTMLSelectElement>) => {
    const newValue = {
      ...this.deserializedValue,
      month: (event.target as HTMLSelectElement).value,
    };

    this.propagateDateChange(newValue);
  };

  private handleChangeYear = (event: FormEvent<HTMLInputElement>) => {
    const newValue = {
      ...this.deserializedValue,
      year: (event.target as HTMLInputElement).value,
    };

    this.propagateDateChange(newValue);
  };

  /**
   * When date is incomplete, it must be stored in state since value is full date string.
   */
  private get deserializedValue(): DeserializedDate {
    if (!/\d{4}-\d{2}-\d{2}/.test(this.props.input.value)) {
      return this.state;
    } else {
      const date = new Date(this.props.input.value);
      return {
        year: String(date.getUTCFullYear()),
        month: String(date.getUTCMonth()),
        day: String(date.getUTCDate()),
      };
    }
  }

  private propagateDateChange(newValue: DeserializedDate) {
    this.setState(newValue);

    const newYear = parseInt(newValue.year, 10);
    const newMonth = parseInt(newValue.month, 10);
    const newDay = parseInt(newValue.day, 10);

    const dateValue = new Date(Date.UTC(newYear, newMonth, newDay));
    // Make sure it's valid by seeing whether inference changed any field
    if (
      !isNaN(dateValue.getTime()) &&
      dateValue.getUTCFullYear() === newYear &&
      dateValue.getUTCMonth() === newMonth &&
      dateValue.getUTCDate() === newDay
    ) {
      const parsedDate = dateValue.toISOString().split('T')[0];
      this.props.input.onChange(parsedDate);
    } else {
      // Must be '', null or undefined doesn't update the form value
      this.props.input.onChange('');
    }
  }
}

export default withTheme(ThemedFormDateInput);
